/* logic-trainer-engine.js — PURE logic core for the /logic-trainer tool.
 *
 * NO DOM, NO window, NO Date.now(), NO Math.random(). Everything is seeded and
 * deterministic so the Node invariant harness can brute-force tens of thousands
 * of puzzles and so share-links reproduce a puzzle byte-for-byte.
 *
 * A PuzzleSpec is fully language-agnostic: it carries enum tokens + numeric vars
 * and template KEYS only — never display strings — so the SAME generated puzzle
 * renders identically in all 9 locales. The app layer resolves keys against the
 * Laravel lang file.
 *
 * Each generator follows: construct -> verify (validate) -> reject & retry ->
 * throw after N attempts. A bad/ambiguous puzzle must surface in QA, never ship.
 *
 * Exposes (UMD): window.LogicTrainerEngine / module.exports.
 */
(function (root, factory) {
  'use strict';
  var E = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = E;
  if (typeof window !== 'undefined') window.LogicTrainerEngine = E;
  else if (root) root.LogicTrainerEngine = E;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  // ───────────────────────── Seeded RNG (mulberry32) ─────────────────────────
  function mulberry32(a) {
    return function () {
      a |= 0; a = (a + 0x6D2B79F5) | 0;
      var t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }
  function makeRng(seed) { return mulberry32((seed >>> 0) || 1); }
  // Deterministic combine of integers into a 32-bit seed (for tests / share-links).
  function hashSeed() {
    var h = 2166136261 >>> 0;
    for (var i = 0; i < arguments.length; i++) {
      h ^= (arguments[i] | 0); h = Math.imul(h, 16777619);
    }
    return h >>> 0;
  }

  // ───────────────────────── Closed vocabulary ─────────────────────────
  // Tokens only — colours/shape glyphs live in logic-trainer-icons.js.
  var SHAPES = ['circle', 'square', 'triangle', 'star', 'heart', 'hexagon', 'diamond'];
  var COLORS = ['red', 'blue', 'green', 'yellow', 'orange', 'purple', 'pink', 'brown'];
  var SIZES = [1, 2, 3];
  var FILLS = ['solid', 'outline', 'striped'];
  var ROTS = [0, 90, 180, 270];
  var ICONS = ['cat', 'dog', 'bird', 'fish', 'apple', 'tree', 'car', 'ball', 'sun', 'flower'];
  // Rotation is only visible on rotationally-asymmetric shapes. Whenever a puzzle
  // VARIES rotation, the shape MUST come from here — else "the turned one" looks
  // identical and the puzzle is unsolvable (a bug the token invariants can't see).
  var ROT_VISIBLE = ['triangle', 'heart', 'star'];
  // Semantic categories for odd-one-out "what kind of thing" rules (top levels).
  var CATEGORIES = {
    animal: ['cat', 'dog', 'bird', 'fish'],
    plant: ['tree', 'apple', 'flower'],
    object: ['car', 'ball', 'sun']
  };

  var SKILLS = ['patterns', 'oddone', 'sort', 'analogy', 'spatial', 'deduce', 'quantity', 'memory'];
  var MAX_LEVEL = 10;

  function baseEl() { return { shape: 'circle', color: 'blue', size: 2, fill: 'solid', rot: 0 }; }
  // Canonical, order-stable key for distinctness / set membership.
  // Grid-shape options (spatial skill) key on their exact (orientation-specific) cells.
  function elKey(el) {
    if (el && el.cells) return 'C:' + canonGrid(el.cells);
    return [el.shape, el.color, el.size, el.fill, el.rot,
      (el.count == null ? '' : el.count), (el.icon == null ? '' : el.icon)].join('|');
  }
  function cloneEl(el) {
    var o = {}; for (var k in el) if (Object.prototype.hasOwnProperty.call(el, k)) o[k] = el[k];
    return o;
  }
  var DIMS = ['shape', 'color', 'size', 'fill', 'rot'];

  // ───────────────────────── RNG helpers ─────────────────────────
  function clamp(v, lo, hi) { return v < lo ? lo : (v > hi ? hi : v); }
  function randint(rng, lo, hi) { return lo + Math.floor(rng() * (hi - lo + 1)); }
  function pick(rng, arr) { return arr[Math.floor(rng() * arr.length)]; }
  function shuffle(rng, arr) {
    var a = arr.slice();
    for (var i = a.length - 1; i > 0; i--) {
      var j = Math.floor(rng() * (i + 1)); var t = a[i]; a[i] = a[j]; a[j] = t;
    }
    return a;
  }
  function sample(rng, arr, k) { return shuffle(rng, arr).slice(0, k); }
  function poolFor(dim) {
    return dim === 'shape' ? SHAPES : dim === 'color' ? COLORS : dim === 'size' ? SIZES
      : dim === 'fill' ? FILLS : dim === 'rot' ? ROTS : SHAPES;
  }
  // Pick a value on `dim` different from `not`.
  function otherVal(rng, dim, not) {
    var pool = poolFor(dim).filter(function (v) { return v !== not; });
    return pick(rng, pool);
  }
  function indexOfKey(arr, el) {
    var k = elKey(el);
    for (var i = 0; i < arr.length; i++) if (elKey(arr[i]) === k) return i;
    return -1;
  }
  function uniqueByKey(arr) {
    var seen = {}, out = [];
    for (var i = 0; i < arr.length; i++) { var k = elKey(arr[i]); if (!seen[k]) { seen[k] = 1; out.push(arr[i]); } }
    return out;
  }

  function genError(skill, msg) {
    return new Error('logic-trainer generator[' + skill + ']: ' + msg);
  }

  // ════════════════════════════════════════════════════════════════════
  //  PATTERNS — "what comes next" (rule induction over an ordered series)
  // ════════════════════════════════════════════════════════════════════
  // Difficulty ladder (level 1..10):
  //   1-2 repeat period2 (color/shape), len4, D2
  //   3-4 repeat period2-3 OR count arithmetic +1, len5, D3
  //   5-6 size/rot cycle OR count arithmetic +2, len6, D3
  //   7-8 two attributes cycling independently, len6-7, D4
  //   9-10 second-order count (changing step), len7, D4
  function patternFamily(level, rng) {
    if (level <= 2) return 'repeat1';
    if (level <= 4) return rng() < 0.5 ? 'repeat2' : 'arith';
    if (level <= 6) return rng() < 0.5 ? 'cycle' : 'arith2';
    if (level <= 8) return 'twoattr';
    return 'twoattr3';
  }
  // Build an independently-periodic series over `dims` (each its own period), used
  // by the two-/three-attribute families. Periods are pool-safe and >= 2 distinct.
  function buildMultiAttr(rng, dims, len) {
    var fixed = baseEl();
    var plans = [];
    for (var di = 0; di < dims.length; di++) {
      var dim = dims[di], pool = poolFor(dim);
      var maxP = Math.min(3, pool.length, len - 3); // detectable from the prefix & pool-safe
      if (maxP < 2) return null;
      var period = randint(rng, 2, maxP);
      plans.push({ dim: dim, vals: sample(rng, pool, period), period: period });
    }
    var series = [];
    for (var i = 0; i < len; i++) {
      var el = cloneEl(fixed);
      for (var p = 0; p < plans.length; p++) el[plans[p].dim] = plans[p].vals[i % plans[p].period];
      series.push(el);
    }
    return series;
  }
  function genPatterns(level, rng) {
    // Series length incl. the answer slot. A period P is only detectable from the
    // visible prefix (length len-1) when len >= P+3 (>=2 confirmations), so start at 5.
    var len = clamp(5 + Math.floor((level - 1) / 2), 5, 8);
    var fam = patternFamily(level, rng);
    var series = [];
    var i, el, n;
    var varyDims = []; // which dims actually change in this series (for salient distractors)

    if (fam === 'repeat1' || fam === 'repeat2' || fam === 'cycle') {
      // periodic on a single dim
      // NO 'size' — size differences read ambiguously for kids (the "right size" is
      // not determinable from the stimulus). Cycle on fill/rotation instead.
      var dim = fam === 'cycle' ? pick(rng, ['fill', 'rot']) : pick(rng, ['color', 'shape']);
      varyDims = [dim];
      var maxP = Math.min(3, len - 3, poolFor(dim).length); // detectable & pool-safe
      var period = fam === 'repeat1' ? 2 : randint(rng, 2, Math.max(2, maxP));
      var cycleVals = sample(rng, poolFor(dim), period);
      var fixed = baseEl();
      // make the constant shape interesting (not always circle) when dim != shape
      if (dim !== 'shape') fixed.shape = pick(rng, SHAPES);
      if (dim === 'rot') fixed.shape = pick(rng, ROT_VISIBLE); // rotation must be visible
      if (dim !== 'color') fixed.color = pick(rng, COLORS);
      for (i = 0; i < len; i++) { el = cloneEl(fixed); el[dim] = cycleVals[i % period]; series.push(el); }
    } else if (fam === 'arith' || fam === 'arith2') {
      var d = fam === 'arith' ? 1 : 2;
      var start = randint(rng, 1, 3);
      var fixedC = baseEl(); fixedC.shape = pick(rng, ['circle', 'star', 'heart', 'square', 'triangle']);
      fixedC.color = pick(rng, COLORS);
      for (i = 0; i < len; i++) {
        n = start + d * i;
        if (n > 9) { len = i; break; }
        el = cloneEl(fixedC); el.count = n; series.push(el);
      }
      if (series.length < 4) return null;
      len = series.length;
    } else if (fam === 'twoattr' || fam === 'twoattr3') {
      var nDims = fam === 'twoattr' ? 2 : 3;
      var dimsN = sample(rng, ['color', 'shape', 'fill'], nDims); // fill (not size) — clearly visible
      series = buildMultiAttr(rng, dimsN, len);
      if (!series) return null;
      varyDims = dimsN.slice();
    }

    var answer = series[len - 1];
    var prefix = series.slice(0, len - 1);

    // Distractors: near-misses that must NOT be plausible continuations.
    var options = [cloneEl(answer)];
    var D = level <= 2 ? 2 : (level <= 6 ? 3 : 3);
    // Perturb only SALIENT dimensions (colour/shape + whatever actually varies), so a
    // distractor is clearly different — never a near-identical size/fill tweak.
    var perturbPool = ['shape', 'color'];
    varyDims.forEach(function (d) { if (perturbPool.indexOf(d) < 0) perturbPool.push(d); });
    var tries = 0;
    while (options.length < D + 1 && tries < 200) {
      tries++;
      var cand = cloneEl(answer);
      var roll = rng();
      if (answer.count != null) {
        // perturb count, or "repeat previous", or naive-linear trap
        if (roll < 0.4) cand.count = clamp(answer.count + (rng() < 0.5 ? 1 : -1), 1, 9);
        else if (roll < 0.7 && prefix.length) cand.count = prefix[prefix.length - 1].count;
        else cand.count = clamp(answer.count + randint(rng, -2, 2), 1, 9);
      } else {
        var pd = pick(rng, perturbPool);
        cand[pd] = otherVal(rng, pd, answer[pd]);
      }
      if (elKey(cand) === elKey(answer)) continue;
      if (isPlausibleNext(prefix, cand)) continue; // never offer a valid continuation as a distractor
      if (indexOfKey(options, cand) >= 0) continue;
      options.push(cand);
    }
    if (options.length < D + 1) return null;

    options = shuffle(rng, options);
    var ansIdx = indexOfKey(options, answer);

    return {
      skill: 'patterns', level: level, family: fam,
      responseKind: 'index',
      promptKey: answer.count != null ? 'prompt.patterns_count' : 'prompt.patterns',
      stimulus: { kind: 'sequence', items: prefix, holeAt: prefix.length },
      options: options, answer: ansIdx,
      rule: { type: fam },
      hintKey: 'hint.patterns', whyKey: 'why.patterns'
    };
  }

  // ── pattern predictors: a continuation is "plausible" if ANY supported rule
  //    consistent with the prefix predicts exactly that next element. Used both
  //    to reject ambiguous distractors and (in validate) to confirm the answer.
  function smallestPeriod(seq) {
    var len = seq.length;
    for (var p = 1; p <= len - 2; p++) { // require >=2 confirmations
      var ok = true;
      for (var i = p; i < len; i++) { if (seq[i] !== seq[i - p]) { ok = false; break; } }
      if (ok) return p;
    }
    return 0;
  }
  function perDimPeriodicPredict(prefix) {
    if (prefix.length < 4) return null;
    var next = cloneEl(prefix[0]);
    var allDims = DIMS.concat(prefix[0].count != null ? ['count'] : []);
    for (var di = 0; di < allDims.length; di++) {
      var dim = allDims[di];
      var seq = prefix.map(function (e) { return e[dim]; });
      var p = smallestPeriod(seq);
      if (!p) return null;
      next[dim] = seq[seq.length - p];
    }
    return next;
  }
  function constExcept(prefix, dim) {
    var od = DIMS.filter(function (d) { return d !== dim; });
    for (var i = 1; i < prefix.length; i++)
      for (var j = 0; j < od.length; j++)
        if (prefix[i][od[j]] !== prefix[0][od[j]]) return false;
    return true;
  }
  function countArithPredict(prefix) {
    if (prefix.length < 3 || prefix[0].count == null || !constExcept(prefix, 'count')) return null;
    var d = prefix[1].count - prefix[0].count;
    for (var i = 2; i < prefix.length; i++) if (prefix[i].count - prefix[i - 1].count !== d) return null;
    var el = cloneEl(prefix[prefix.length - 1]); el.count = el.count + d; return el;
  }
  function countSecondOrderPredict(prefix) {
    if (prefix.length < 4 || prefix[0].count == null || !constExcept(prefix, 'count')) return null;
    var diffs = [];
    for (var i = 1; i < prefix.length; i++) diffs.push(prefix[i].count - prefix[i - 1].count);
    var dd = diffs[1] - diffs[0];
    for (var j = 2; j < diffs.length; j++) if (diffs[j] - diffs[j - 1] !== dd) return null;
    var nextDiff = diffs[diffs.length - 1] + dd;
    var el = cloneEl(prefix[prefix.length - 1]); el.count = el.count + nextDiff; return el;
  }
  function fittingPredictions(prefix) {
    var preds = [], p;
    if ((p = perDimPeriodicPredict(prefix))) preds.push(p);
    if ((p = countArithPredict(prefix))) preds.push(p);
    if ((p = countSecondOrderPredict(prefix))) preds.push(p);
    return uniqueByKey(preds);
  }
  function isPlausibleNext(prefix, cand) {
    var preds = fittingPredictions(prefix), k = elKey(cand);
    for (var i = 0; i < preds.length; i++) if (elKey(preds[i]) === k) return true;
    return false;
  }

  function validatePatterns(spec) {
    var prefix = spec.stimulus.items, opts = spec.options, ans = opts[spec.answer];
    var preds = fittingPredictions(prefix);
    if (!preds.length) return 'no fitting rule for prefix';
    // answer must be a plausible continuation
    if (!isPlausibleNext(prefix, ans)) return 'answer not predicted by any rule';
    // exactly ONE option may be a plausible continuation (the answer)
    var n = 0;
    for (var i = 0; i < opts.length; i++) if (isPlausibleNext(prefix, opts[i])) n++;
    if (n !== 1) return 'ambiguous: ' + n + ' options are valid continuations';
    return null;
  }

  // ════════════════════════════════════════════════════════════════════
  //  ODD-ONE-OUT — classification + negation
  // ════════════════════════════════════════════════════════════════════
  // Rule dimensions are categorical only — NO size/rotation. Size differences are
  // too subtle and rotation reads as "different shape", both creating a second
  // visually-odd item (ambiguous for a 6yo). Difficulty grows via item count,
  // subtler rule values (fill), and semantic categories at the top.
  function oddDimPool(level) {
    if (level <= 2) return ['color', 'shape'];
    if (level <= 4) return ['color', 'shape'];
    if (level <= 6) return ['shape', 'fill'];
    if (level <= 8) return ['fill', 'color'];
    return ['category', 'fill'];
  }
  // Assign K values from pool so every USED value appears >= 2 times (no singleton).
  function assignNoSingleton(rng, K, pool) {
    if (K <= 3) { var v = pick(rng, pool); return new Array(K).fill(v); }
    var two = sample(rng, pool, 2);
    var cntA = randint(rng, 2, K - 2); // both halves >= 2
    var arr = [];
    for (var i = 0; i < cntA; i++) arr.push(two[0]);
    for (var j = cntA; j < K; j++) arr.push(two[1]);
    return shuffle(rng, arr);
  }
  function genOddone(level, rng) {
    var K = clamp(3 + Math.floor((level - 1) / 2), 3, 6);
    var pool = oddDimPool(level);
    var ruleDim = pick(rng, pool);
    var items = [], i;
    var oddIndex = randint(rng, 0, K - 1);

    if (ruleDim === 'category') {
      // K-1 items from one category, 1 from another (icons)
      var cats = Object.keys(CATEGORIES);
      var inCat = pick(rng, cats);
      var outCat = otherVal(rng, '_cat', inCat); // pick different category
      outCat = pick(rng, cats.filter(function (c) { return c !== inCat; }));
      var inIcons = shuffle(rng, CATEGORIES[inCat]);
      for (i = 0; i < K; i++) {
        var e = baseEl(); e.shape = 'circle'; e.color = 'blue';
        e.icon = (i === oddIndex) ? pick(rng, CATEGORIES[outCat]) : inIcons[i % inIcons.length];
        delete e.shape; // icon-only render
        items.push(e);
      }
      // ensure in-category icons are not all identical singletons issue: icons may repeat -> fine
      // Guarantee no accidental icon singleton among the K-1 group (need each group icon >=2 OR treat category as rule)
      // For category rule, the "value" is the category; build report on category dimension below.
    } else {
      var shared = pick(rng, poolFor(ruleDim));
      var oddVal = otherVal(rng, ruleDim, shared);
      var base = baseEl();
      // a non-trivial constant backdrop
      if (ruleDim !== 'shape') base.shape = pick(rng, SHAPES);
      if (ruleDim !== 'color') base.color = pick(rng, COLORS);
      for (i = 0; i < K; i++) { var it = cloneEl(base); it[ruleDim] = (i === oddIndex) ? oddVal : shared; items.push(it); }
      // Controlled noise — ONLY colour/fill (never shape/size/rot, which look like a
      // second "odd" item and confuse the child). Each value appears >=2 (no singleton).
      var noiseDims = [];
      if (level >= 5 && K >= 4) {
        var cand = ['color', 'fill'].filter(function (d) { return d !== ruleDim; });
        noiseDims = sample(rng, cand, 1);
        for (var nd = 0; nd < noiseDims.length; nd++) {
          var vals = assignNoSingleton(rng, K, poolFor(noiseDims[nd]));
          for (i = 0; i < K; i++) items[i][noiseDims[nd]] = vals[i];
        }
      }
    }

    return {
      skill: 'oddone', level: level, responseKind: 'index',
      optionsDistinct: false,   // group items are intentionally identical
      promptKey: 'prompt.oddone',
      stimulus: { kind: 'set', items: items },
      options: items,           // options ARE the items (pick the odd one)
      answer: oddIndex,
      rule: { type: 'oddone', dim: ruleDim },
      hintKey: 'hint.oddone', hintVars: { attr: ruleDim === 'category' ? 'kind' : ruleDim },
      whyKey: 'why.oddone'
    };
  }
  // Per-dimension singleton report: dim -> array of indices that are unique on that dim.
  function singletonReport(items, dims) {
    var rep = {};
    for (var d = 0; d < dims.length; d++) {
      var dim = dims[d], byVal = {};
      for (var i = 0; i < items.length; i++) {
        var v = items[i][dim]; if (v == null) continue;
        (byVal[v] = byVal[v] || []).push(i);
      }
      var singles = [];
      for (var key in byVal) if (byVal[key].length === 1) singles.push(byVal[key][0]);
      rep[dim] = singles;
    }
    return rep;
  }
  function validateOddone(spec) {
    var items = spec.options, ans = spec.answer;
    if (spec.rule.dim === 'category') {
      // category dimension: each item's category; exactly one item in a lone category
      var catOf = function (ic) { for (var c in CATEGORIES) if (CATEGORIES[c].indexOf(ic) >= 0) return c; return '?'; };
      var byCat = {};
      for (var i = 0; i < items.length; i++) { var c = catOf(items[i].icon); (byCat[c] = byCat[c] || []).push(i); }
      var singles = [];
      for (var k in byCat) if (byCat[k].length === 1) singles.push(byCat[k][0]);
      if (singles.length !== 1 || singles[0] !== ans) return 'category rule not unique';
      // and no raw-icon singleton isolating a DIFFERENT item
      var iconRep = singletonReport(items, ['icon']);
      for (var z = 0; z < iconRep.icon.length; z++) if (iconRep.icon[z] !== ans) return 'icon singleton conflicts';
      return null;
    }
    var rep = singletonReport(items, DIMS);
    var totalSingles = [];
    for (var dim in rep) for (var j = 0; j < rep[dim].length; j++) totalSingles.push(dim + ':' + rep[dim][j]);
    // exactly one singleton overall, on the rule dim, at the answer
    var onRule = rep[spec.rule.dim] || [];
    if (onRule.length !== 1 || onRule[0] !== ans) return 'rule dim not a unique singleton at answer';
    for (var d2 in rep) if (d2 !== spec.rule.dim && rep[d2].length !== 0)
      return 'extra singleton on ' + d2;
    return null;
  }

  // ════════════════════════════════════════════════════════════════════
  //  ANALOGY — A : B :: C : ?   (transfer a relation across a pair)
  // ════════════════════════════════════════════════════════════════════
  var ANA_DIMS = ['shape', 'color', 'size', 'fill', 'rot', 'count'];
  function diffDims(a, b) {
    var d = [];
    for (var i = 0; i < ANA_DIMS.length; i++) {
      var k = ANA_DIMS[i];
      if ((a[k] == null ? null : a[k]) !== (b[k] == null ? null : b[k])) d.push(k);
    }
    return d;
  }
  function applyRel(rel, el) {
    var o = cloneEl(el);
    switch (rel.type) {
      case 'recolor': o.color = rel.color; break;
      case 'reshape': o.shape = rel.shape; break;
      case 'refill': o.fill = rel.fill; break;
      case 'grow': o.size = Math.min(3, o.size + 1); break;
      case 'shrink': o.size = Math.max(1, o.size - 1); break;
      case 'rotate': o.rot = (o.rot + 90) % 360; break;
      case 'addcount': o.count = clamp((o.count || 1) + rel.k, 1, 9); break;
    }
    return o;
  }
  // The single allowed relation (if any) that maps A->B exactly.
  function inferRelations(A, B) {
    var d = diffDims(A, B);
    if (d.length !== 1) return [];
    var dim = d[0], out = [];
    if (dim === 'color') out.push({ type: 'recolor', color: B.color });
    else if (dim === 'shape') out.push({ type: 'reshape', shape: B.shape });
    else if (dim === 'fill') out.push({ type: 'refill', fill: B.fill });
    else if (dim === 'size') { if (B.size === A.size + 1) out.push({ type: 'grow' }); else if (B.size === A.size - 1) out.push({ type: 'shrink' }); }
    else if (dim === 'rot') { if ((A.rot + 90) % 360 === B.rot) out.push({ type: 'rotate' }); }
    else if (dim === 'count') { out.push({ type: 'addcount', k: B.count - A.count }); }
    return out;
  }
  function analogyRelPool(level) {
    if (level <= 3) return ['recolor', 'reshape', 'grow', 'shrink'];
    if (level <= 6) return ['recolor', 'reshape', 'grow', 'shrink', 'rotate', 'refill', 'addcount'];
    return ['recolor', 'reshape', 'grow', 'shrink', 'rotate', 'refill', 'addcount'];
  }
  var REL_DIM = { recolor: 'color', reshape: 'shape', refill: 'fill', grow: 'size', shrink: 'size', rotate: 'rot', addcount: 'count' };
  function genAnalogy(level, rng) {
    var relType = pick(rng, analogyRelPool(level));
    var countMode = relType === 'addcount';
    var A = baseEl();
    A.shape = pick(rng, SHAPES); A.color = pick(rng, COLORS); A.fill = pick(rng, FILLS); A.rot = pick(rng, ROTS); A.size = randint(rng, 1, 3);
    if (countMode) A.count = randint(rng, 1, 4);

    var rel;
    if (relType === 'grow') { A.size = randint(rng, 1, 2); rel = { type: 'grow' }; }
    else if (relType === 'shrink') { A.size = randint(rng, 2, 3); rel = { type: 'shrink' }; }
    else if (relType === 'recolor') rel = { type: 'recolor', color: otherVal(rng, 'color', A.color) };
    else if (relType === 'reshape') rel = { type: 'reshape', shape: otherVal(rng, 'shape', A.shape) };
    else if (relType === 'refill') rel = { type: 'refill', fill: otherVal(rng, 'fill', A.fill) };
    else if (relType === 'rotate') { A.shape = pick(rng, ROT_VISIBLE); rel = { type: 'rotate' }; }
    else { rel = { type: 'addcount', k: pick(rng, [1, 2]) }; A.count = randint(rng, 1, 9 - rel.k - 1); if (A.count < 1) A.count = 1; }

    var rdim = REL_DIM[relType];
    // C = A but differ in one NON-relation dim (so answer != B) and same rel-dim value.
    // Exclude 'rot' (invisible on round shapes) and, for the rotate relation, 'shape'
    // (must stay rotation-visible) — the differentiator must always be visible.
    var extraPool = ['shape', 'color', 'size', 'fill'].filter(function (x) { return x !== rdim && !(relType === 'rotate' && x === 'shape'); });
    var extraDim = pick(rng, extraPool);
    var C = cloneEl(A);
    C[extraDim] = otherVal(rng, extraDim, A[extraDim]);

    var B = applyRel(rel, A);
    var answer = applyRel(rel, C);
    if (elKey(B) === elKey(A)) return null;            // relation must be visible
    if (elKey(answer) === elKey(C)) return null;        // relation must change C too
    if (elKey(answer) === elKey(B)) return null;        // answer must differ from B

    // Distractors encode the three classic analogy errors.
    var dB = cloneEl(B), dC = cloneEl(C), dWrong = null;
    var wrongPool = analogyRelPool(level).filter(function (t) { return REL_DIM[t] !== rdim; });
    for (var w = 0; w < 8 && !dWrong; w++) {
      var r2t = pick(rng, wrongPool);
      var r2 = r2t === 'recolor' ? { type: 'recolor', color: otherVal(rng, 'color', C.color) }
        : r2t === 'reshape' ? { type: 'reshape', shape: otherVal(rng, 'shape', C.shape) }
          : r2t === 'refill' ? { type: 'refill', fill: otherVal(rng, 'fill', C.fill) }
            : r2t === 'grow' ? { type: 'grow' } : r2t === 'shrink' ? { type: 'shrink' }
              : r2t === 'rotate' ? { type: 'rotate' } : { type: 'addcount', k: 1 };
      if ((r2t === 'addcount') && C.count == null) continue;
      var cand = applyRel(r2, C);
      var ks = [elKey(answer), elKey(dB), elKey(dC)];
      if (elKey(cand) === elKey(C)) continue; // must visibly change
      if (ks.indexOf(elKey(cand)) >= 0) continue;
      dWrong = cand;
    }
    if (!dWrong) return null;

    var options = [cloneEl(answer), dB, dC, dWrong];
    if (uniqueByKey(options).length !== 4) return null;
    options = shuffle(rng, options);
    var ansIdx = indexOfKey(options, answer);

    return {
      skill: 'analogy', level: level, responseKind: 'index',
      promptKey: countMode ? 'prompt.analogy_count' : 'prompt.analogy',
      stimulus: { kind: 'analogy', a: A, b: B, c: C },
      options: options, answer: ansIdx,
      rule: { type: 'analogy', rel: relType },
      hintKey: 'hint.analogy', whyKey: 'why.analogy'
    };
  }
  function validateAnalogy(spec) {
    var st = spec.stimulus, opts = spec.options;
    var rels = inferRelations(st.a, st.b);
    if (rels.length !== 1) return 'A->B not explained by exactly one relation (' + rels.length + ')';
    var expected = applyRel(rels[0], st.c);
    // exactly one option is the consistent answer, and it is spec.answer
    var hits = [];
    for (var i = 0; i < opts.length; i++) if (elKey(opts[i]) === elKey(expected)) hits.push(i);
    if (hits.length !== 1 || hits[0] !== spec.answer) return 'answer is not the unique consistent continuation';
    return null;
  }

  // ════════════════════════════════════════════════════════════════════
  //  QUANTITY — compare / order / transitive (seriation & magnitude logic)
  // ════════════════════════════════════════════════════════════════════
  function quantitySub(level, rng) {
    if (level <= 3) return 'compare';
    if (level <= 6) return rng() < 0.5 ? 'compare' : 'order';
    if (level <= 8) return 'order';
    return 'transitive';
  }
  function genQuantity(level, rng) {
    var sub = quantitySub(level, rng);
    if (sub === 'compare') {
      var goal = (level >= 5 && rng() < 0.4) ? 'fewer' : 'more';
      var gap = level <= 3 ? randint(rng, 2, 4) : (level <= 5 ? 1 : randint(rng, 1, 2));
      var lo = randint(rng, 1, 9 - gap), hi = lo + gap;
      var shape = pick(rng, ['circle', 'star', 'heart', 'square']);
      var color = pick(rng, COLORS);
      var bigFirst = rng() < 0.5;
      var c1 = bigFirst ? hi : lo, c2 = bigFirst ? lo : hi;
      var g1 = { shape: shape, color: color, size: 2, fill: 'solid', count: c1 };
      var g2 = { shape: shape, color: color, size: 2, fill: 'solid', count: c2 };
      if (level >= 6) { // conservation conflict: more dots rendered smaller
        if (c1 > c2) { g1.size = 1; g2.size = 3; } else { g1.size = 3; g2.size = 1; }
      }
      var maxIdx = c1 > c2 ? 0 : 1, minIdx = c1 > c2 ? 1 : 0;
      return {
        skill: 'quantity', level: level, responseKind: 'index',
        promptKey: goal === 'more' ? 'prompt.quantity_more' : 'prompt.quantity_fewer',
        stimulus: { kind: 'compare', items: [g1, g2] },
        options: [g1, g2], answer: goal === 'more' ? maxIdx : minIdx,
        rule: { type: 'compare', goal: goal },
        hintKey: 'hint.quantity_compare', whyKey: 'why.quantity'
      };
    }
    if (sub === 'order') {
      var dir = rng() < 0.5 ? 'asc' : 'desc';
      var K = clamp(3 + Math.floor((level - 3) / 2), 3, 6);
      var attr = (K <= 3 && rng() < 0.5) ? 'size' : 'count';
      var vals;
      if (attr === 'size') { vals = sample(rng, [1, 2, 3], 3); K = 3; }
      else { vals = sample(rng, [1, 2, 3, 4, 5, 6, 7, 8, 9], K); }
      var sh = pick(rng, ['circle', 'star', 'heart', 'square', 'triangle']);
      var col = pick(rng, COLORS);
      var items = vals.map(function (v) {
        var e = { shape: sh, color: col, size: 2, fill: 'solid' };
        if (attr === 'size') e.size = v; else e.count = v;
        return e;
      });
      var idx = items.map(function (_, i) { return i; });
      idx.sort(function (a, b) { return (items[a][attr] - items[b][attr]) * (dir === 'asc' ? 1 : -1); });
      return {
        skill: 'quantity', level: level, responseKind: 'order',
        promptKey: dir === 'asc' ? 'prompt.quantity_order_asc' : 'prompt.quantity_order_desc',
        stimulus: { kind: 'order', items: items, attr: attr, dir: dir },
        answer: idx,
        rule: { type: 'order', attr: attr, dir: dir },
        hintKey: 'hint.quantity_order', whyKey: 'why.quantity'
      };
    }
    // transitive: chain of comparisons, find biggest / smallest
    var N = level <= 9 ? 3 : 4;
    var palette = shuffle(rng, [['triangle', 'red'], ['circle', 'blue'], ['square', 'green'], ['star', 'orange'], ['heart', 'purple']]).slice(0, N);
    var entities = palette.map(function (p) { return { shape: p[0], color: p[1], size: 2, fill: 'solid' }; });
    var order = shuffle(rng, entities.map(function (_, i) { return i; })); // order[0] = biggest
    var clues = [];
    for (var i = 0; i < N - 1; i++) clues.push([order[i], order[i + 1]]); // i greater than i+1
    var ask = rng() < 0.5 ? 'biggest' : 'smallest';
    var ansIdx = ask === 'biggest' ? order[0] : order[N - 1];
    return {
      skill: 'quantity', level: level, responseKind: 'index',
      promptKey: ask === 'biggest' ? 'prompt.quantity_biggest' : 'prompt.quantity_smallest',
      stimulus: { kind: 'transitive', entities: entities, clues: shuffle(rng, clues), ask: ask },
      options: entities, answer: ansIdx,
      rule: { type: 'transitive', ask: ask, nclues: N - 1 },
      hintKey: 'hint.quantity_transitive', whyKey: 'why.quantity'
    };
  }
  function validateQuantity(spec) {
    var st = spec.stimulus, r = spec.rule;
    if (r.type === 'compare') {
      var c0 = st.items[0].count, c1 = st.items[1].count;
      if (c0 === c1) return 'compare counts equal';
      var maxIdx = c0 > c1 ? 0 : 1, minIdx = c0 > c1 ? 1 : 0;
      var want = r.goal === 'more' ? maxIdx : minIdx;
      if (spec.answer !== want) return 'compare answer wrong';
      return null;
    }
    if (r.type === 'order') {
      var items = st.items, attr = r.attr;
      var set = {};
      for (var i = 0; i < items.length; i++) { if (set[items[i][attr]]) return 'order values not distinct'; set[items[i][attr]] = 1; }
      var idx = items.map(function (_, k) { return k; });
      idx.sort(function (a, b) { return (items[a][attr] - items[b][attr]) * (r.dir === 'asc' ? 1 : -1); });
      if (idx.join(',') !== spec.answer.join(',')) return 'order answer mismatch';
      // a swapped order must check incorrect
      if (spec.answer.length >= 2) {
        var bad = spec.answer.slice(); var t = bad[0]; bad[0] = bad[1]; bad[1] = t;
        if (check(spec, bad).correct) return 'swapped order accepted';
      }
      return null;
    }
    // transitive: clues must yield a unique total order (single chain)
    var N = st.entities.length, clues = st.clues;
    if (clues.length !== N - 1) return 'transitive wrong clue count';
    var indeg = new Array(N).fill(0), outAdj = {};
    for (var c = 0; c < clues.length; c++) { var a = clues[c][0], b = clues[c][1]; indeg[b]++; (outAdj[a] = outAdj[a] || []).push(b); }
    var sources = []; for (var s = 0; s < N; s++) if (indeg[s] === 0) sources.push(s);
    if (sources.length !== 1) return 'transitive: not a unique source';
    // walk the chain; must visit all N exactly once
    var seen = {}, node = sources[0], visited = 0;
    while (node != null && !seen[node]) { seen[node] = 1; visited++; var nx = outAdj[node]; if (nx && nx.length > 1) return 'transitive: branching'; node = nx ? nx[0] : null; }
    if (visited !== N) return 'transitive: clues do not form a total chain';
    var top = sources[0], bottom = node == null ? null : null;
    // bottom = the entity with no outgoing
    var bot = -1; for (var z = 0; z < N; z++) if (!outAdj[z] || outAdj[z].length === 0) { if (bot >= 0) return 'transitive: multiple sinks'; bot = z; }
    var want2 = st.ask === 'biggest' ? top : bot;
    if (spec.answer !== want2) return 'transitive answer wrong';
    return null;
  }

  // ════════════════════════════════════════════════════════════════════
  //  SPATIAL — mental rotation / mirror / grid completion (polyomino shapes)
  // ════════════════════════════════════════════════════════════════════
  function normGrid(cells) {
    var minR = Infinity, minC = Infinity, i;
    for (i = 0; i < cells.length; i++) { if (cells[i][0] < minR) minR = cells[i][0]; if (cells[i][1] < minC) minC = cells[i][1]; }
    var n = cells.map(function (c) { return [c[0] - minR, c[1] - minC]; });
    n.sort(function (a, b) { return a[0] - b[0] || a[1] - b[1]; });
    return n;
  }
  function canonGrid(cells) { return normGrid(cells).map(function (c) { return c[0] + ',' + c[1]; }).join(' '); }
  function rot90cells(cells) { return normGrid(cells.map(function (c) { return [c[1], -c[0]]; })); }
  function mirrorCells(cells) { return normGrid(cells.map(function (c) { return [c[0], -c[1]]; })); }
  function rotateN(cells, t) { t = ((t % 4) + 4) % 4; var c = cells; for (var i = 0; i < t; i++) c = rot90cells(c); return c; }
  function rotKey(cells) { var best = null; for (var t = 0; t < 4; t++) { var k = canonGrid(rotateN(cells, t)); if (best === null || k < best) best = k; } return best; }
  function distinctRots(cells) { var s = {}; for (var t = 0; t < 4; t++) s[canonGrid(rotateN(cells, t))] = 1; return Object.keys(s).length; }
  function isChiral(cells) { return rotKey(cells) !== rotKey(mirrorCells(cells)); }
  function randomPolyomino(rng, n) {
    var cells = [[0, 0]], occ = { '0,0': 1 };
    var dirs = [[1, 0], [-1, 0], [0, 1], [0, -1]];
    while (cells.length < n) {
      var frontier = [];
      for (var i = 0; i < cells.length; i++) for (var d = 0; d < 4; d++) {
        var r = cells[i][0] + dirs[d][0], c = cells[i][1] + dirs[d][1], k = r + ',' + c;
        if (!occ[k]) frontier.push([r, c]);
      }
      if (!frontier.length) break;
      var p = frontier[Math.floor(rng() * frontier.length)];
      occ[p[0] + ',' + p[1]] = 1; cells.push(p);
    }
    return normGrid(cells);
  }
  function spatialMode(level) { return level <= 4 ? 'rotation' : (level <= 7 ? 'mirror' : 'grid'); }
  var GRID_STEPS = [[1, 2], [2, 1], [2, 3], [3, 2]]; // each yields the 4 distinct rotations {0,1,2,3}
  function genSpatial(level, rng) {
    var mode = spatialMode(level);
    var n = level <= 4 ? 4 : 5, i;
    var base = null;
    for (var t = 0; t < 80; t++) {
      var cand = randomPolyomino(rng, n);
      if (cand.length < n) continue;
      if (!isChiral(cand)) continue;                    // mirror must be a genuine "different piece"
      if (mode === 'grid' && distinctRots(cand) !== 4) continue;
      if (mode === 'mirror' && distinctRots(cand) < 3) continue;
      base = cand; break;
    }
    if (!base) return null;
    var opts, answerCells, usedG, k, rc;

    if (mode === 'rotation') {
      var ks = [1, 2, 3].filter(function (kk) { return canonGrid(rotateN(base, kk)) !== canonGrid(base); });
      if (!ks.length) return null;
      answerCells = rotateN(base, pick(rng, ks));
      opts = [{ cells: answerCells }]; usedG = {}; usedG[canonGrid(answerCells)] = 1;
      var mir = rotateN(mirrorCells(base), randint(rng, 0, 3));
      if (rotKey(mir) !== rotKey(base) && !usedG[canonGrid(mir)]) { usedG[canonGrid(mir)] = 1; opts.push({ cells: mir }); }
      var guard = 0;
      while (opts.length < 4 && guard++ < 120) {
        var other = randomPolyomino(rng, n);
        if (other.length < n) continue;
        if (rotKey(other) === rotKey(base)) continue;   // distractor must NOT be the same piece
        rc = rotateN(other, randint(rng, 0, 3));
        if (usedG[canonGrid(rc)]) continue;
        usedG[canonGrid(rc)] = 1; opts.push({ cells: rc });
      }
      if (opts.length < 4) return null;
      opts = shuffle(rng, opts);
      return {
        skill: 'spatial', level: level, family: 'srot', responseKind: 'index',
        promptKey: 'prompt.spatial_rotation',
        stimulus: { kind: 'spatial', mode: 'rotation', base: { cells: base } },
        options: opts, answer: indexOfKey(opts, { cells: answerCells }),
        rule: { type: 'spatial', mode: 'rotation' },
        hintKey: 'hint.spatial_rotation', whyKey: 'why.spatial'
      };
    }

    if (mode === 'mirror') {
      answerCells = rotateN(mirrorCells(base), randint(rng, 0, 3));
      opts = [{ cells: answerCells }]; usedG = {}; usedG[canonGrid(answerCells)] = 1;
      var rots = shuffle(rng, [0, 1, 2, 3]);
      for (i = 0; i < rots.length && opts.length < 4; i++) {
        rc = rotateN(base, rots[i]);
        if (usedG[canonGrid(rc)]) continue;
        if (rotKey(rc) === rotKey(answerCells)) continue; // safety (chiral guarantees this won't hit)
        usedG[canonGrid(rc)] = 1; opts.push({ cells: rc });
      }
      if (opts.length < 4) return null;
      opts = shuffle(rng, opts);
      return {
        skill: 'spatial', level: level, family: 'smirror', responseKind: 'index',
        promptKey: 'prompt.spatial_mirror',
        stimulus: { kind: 'spatial', mode: 'mirror', base: { cells: base } },
        options: opts, answer: indexOfKey(opts, { cells: answerCells }),
        rule: { type: 'spatial', mode: 'mirror' },
        hintKey: 'hint.spatial_mirror', whyKey: 'why.spatial'
      };
    }

    // grid completion: 2x2, each cell a distinct rotation; find the missing one.
    var step = pick(rng, GRID_STEPS), colStep = step[0], rowStep = step[1];
    var cellAt = function (r, c) { return rotateN(base, c * colStep + r * rowStep); };
    var matrix = [
      [{ cells: cellAt(0, 0) }, { cells: cellAt(0, 1) }],
      [{ cells: cellAt(1, 0) }, null]
    ];
    answerCells = cellAt(1, 1);
    opts = [{ cells: answerCells }]; usedG = {}; usedG[canonGrid(answerCells)] = 1;
    for (k = 0; k < 4 && opts.length < 4; k++) { rc = rotateN(base, k); if (!usedG[canonGrid(rc)]) { usedG[canonGrid(rc)] = 1; opts.push({ cells: rc }); } }
    if (opts.length < 4) return null;
    opts = shuffle(rng, opts);
    return {
      skill: 'spatial', level: level, family: 'sgrid', responseKind: 'index',
      promptKey: 'prompt.spatial_grid',
      stimulus: { kind: 'spatial', mode: 'grid', matrix: matrix, rows: 2, cols: 2 },
      options: opts, answer: indexOfKey(opts, { cells: answerCells }),
      rule: { type: 'spatial', mode: 'grid', base: { cells: base }, colStep: colStep, rowStep: rowStep },
      hintKey: 'hint.spatial_grid', whyKey: 'why.spatial'
    };
  }
  function validateSpatial(spec) {
    var r = spec.rule, opts = spec.options, ans = opts[spec.answer], i, n;
    if (r.mode === 'rotation') {
      var base = spec.stimulus.base.cells;
      if (!isChiral(base)) return 'rotation: base is achiral (mirror ambiguous)';
      if (canonGrid(ans.cells) === canonGrid(base)) return 'rotation: answer identical to base (not turned)';
      var bk = rotKey(base); n = 0;
      for (i = 0; i < opts.length; i++) if (rotKey(opts[i].cells) === bk) n++;
      if (n !== 1 || rotKey(ans.cells) !== bk) return 'rotation: not exactly one same-piece option';
      return null;
    }
    if (r.mode === 'mirror') {
      var base2 = spec.stimulus.base.cells;
      if (!isChiral(base2)) return 'mirror: base achiral';
      var mk = rotKey(mirrorCells(base2)), bk2 = rotKey(base2);
      if (mk === bk2) return 'mirror: classes coincide';
      n = 0; for (i = 0; i < opts.length; i++) if (rotKey(opts[i].cells) === mk) n++;
      if (n !== 1 || rotKey(ans.cells) !== mk) return 'mirror: not exactly one mirrored option';
      return null;
    }
    // grid
    var gb = r.base.cells;
    if (distinctRots(gb) !== 4) return 'grid: base not rotationally asymmetric';
    var expected = canonGrid(rotateN(gb, 1 * r.colStep + 1 * r.rowStep));
    if (canonGrid(ans.cells) !== expected) return 'grid: answer not the rule continuation';
    n = 0; for (i = 0; i < opts.length; i++) if (canonGrid(opts[i].cells) === expected) n++;
    if (n !== 1) return 'grid: ' + n + ' options satisfy the rule';
    // every visible matrix cell must follow the rotation rule
    var m = spec.stimulus.matrix;
    for (var rr = 0; rr < 2; rr++) for (var cc = 0; cc < 2; cc++) {
      var cell = m[rr][cc]; if (!cell) continue;
      if (canonGrid(cell.cells) !== canonGrid(rotateN(gb, cc * r.colStep + rr * r.rowStep))) return 'grid: visible cell breaks rule';
    }
    return null;
  }

  // ════════════════════════════════════════════════════════════════════
  //  DEDUCE — combine clues to force a single answer (the heart of deduction)
  // ════════════════════════════════════════════════════════════════════
  function clueEval(clue, el) {
    switch (clue.type) {
      case 'is': return el[clue.dim] === clue.value;
      case 'isnot': return el[clue.dim] !== clue.value;
      case 'bigger': return el.size > clue.value;
      case 'smaller': return el.size < clue.value;
      default: return false;
    }
  }
  function deduceTypes(level) {
    if (level <= 3) return ['is'];
    if (level <= 6) return ['is', 'isnot'];
    return ['is', 'isnot', 'bigger', 'smaller'];
  }
  function deduceDims(level) {
    if (level <= 3) return ['shape', 'color'];
    if (level <= 6) return ['shape', 'color', 'size'];
    return ['shape', 'color', 'size', 'fill'];
  }
  function candidateClues(survivors, target, types, usedDims) {
    var cands = [], di, d;
    for (di = 0; di < usedDims.length; di++) {
      d = usedDims[di];
      if (types.indexOf('is') >= 0) cands.push({ type: 'is', dim: d, value: target[d] });
      if (types.indexOf('isnot') >= 0) {
        var vals = {};
        for (var s = 0; s < survivors.length; s++) if (survivors[s][d] !== target[d]) vals[survivors[s][d]] = 1;
        for (var v in vals) cands.push({ type: 'isnot', dim: d, value: (d === 'size' ? +v : v) });
      }
    }
    if (usedDims.indexOf('size') >= 0) {
      if (types.indexOf('bigger') >= 0) for (var b = 1; b < target.size; b++) cands.push({ type: 'bigger', value: b });
      if (types.indexOf('smaller') >= 0) for (var sm = target.size + 1; sm <= 3; sm++) cands.push({ type: 'smaller', value: sm });
    }
    return cands;
  }
  function genDeduce(level, rng) {
    var usedDims = deduceDims(level), types = deduceTypes(level), i, d;
    var pools = {};
    for (i = 0; i < usedDims.length; i++) {
      d = usedDims[i];
      pools[d] = d === 'size' ? [1, 2, 3] : sample(rng, poolFor(d), Math.min(3, poolFor(d).length));
    }
    var M = clamp(3 + Math.floor((level - 1) / 2), 3, 6);
    var universe = [], seen = {}, guard = 0;
    while (universe.length < M && guard++ < 300) {
      var el = baseEl();
      for (i = 0; i < usedDims.length; i++) el[usedDims[i]] = pick(rng, pools[usedDims[i]]);
      var key = usedDims.map(function (x) { return el[x]; }).join('|');
      if (seen[key]) continue; seen[key] = 1; universe.push(el);
    }
    if (universe.length < M) return null;

    var target = pick(rng, universe);
    var survivors = universe.slice(), clues = [], safety = 0;
    while (survivors.length > 1 && safety++ < 40) {
      var cands = candidateClues(survivors, target, types, usedDims).filter(function (c) { return clueEval(c, target); });
      var reducing = [];
      for (i = 0; i < cands.length; i++) {
        var after = survivors.filter(function (s) { return clueEval(cands[i], s); });
        if (after.length < survivors.length && after.length >= 1) reducing.push({ c: cands[i], after: after });
      }
      if (!reducing.length) return null;
      var chosen = pick(rng, reducing);
      clues.push(chosen.c); survivors = chosen.after;
    }
    if (survivors.length !== 1 || survivors[0] !== target) return null;

    // Minimise: drop any clue that isn't needed for uniqueness (no confusing redundancy).
    var changed = true;
    while (changed) {
      changed = false;
      for (i = 0; i < clues.length; i++) {
        var without = clues.slice(0, i).concat(clues.slice(i + 1));
        var surv = universe.filter(function (e) { return without.every(function (c) { return clueEval(c, e); }); });
        if (surv.length === 1 && surv[0] === target) { clues = without; changed = true; break; }
      }
    }
    if (!clues.length) return null;

    var opts = shuffle(rng, universe.slice());
    return {
      skill: 'deduce', level: level, responseKind: 'index',
      promptKey: 'prompt.deduce',
      stimulus: { kind: 'deduce', items: opts, clues: clues, usedDims: usedDims },
      options: opts, answer: indexOfKey(opts, target),
      rule: { type: 'deduce', nclues: clues.length },
      hintKey: 'hint.deduce', whyKey: 'why.deduce'
    };
  }
  function validateDeduce(spec) {
    var items = spec.stimulus.items, clues = spec.stimulus.clues, ans = spec.answer, i;
    if (!clues.length) return 'deduce: no clues';
    var survivors = [];
    for (i = 0; i < items.length; i++) if (clues.every(function (c) { return clueEval(c, items[i]); })) survivors.push(i);
    if (survivors.length !== 1) return 'deduce: ' + survivors.length + ' items satisfy all clues';
    if (survivors[0] !== ans) return 'deduce: unique survivor is not the answer';
    // every clue must be true of the answer (consistency)
    for (i = 0; i < clues.length; i++) if (!clueEval(clues[i], items[ans])) return 'deduce: clue false of answer';
    // minimality: removing any single clue must leave >1 survivor (no redundant clue)
    for (i = 0; i < clues.length; i++) {
      var without = clues.slice(0, i).concat(clues.slice(i + 1));
      var n = 0;
      for (var j = 0; j < items.length; j++) if (without.every(function (c) { return clueEval(c, items[j]); })) n++;
      if (n === 1) return 'deduce: redundant clue ' + i;
    }
    return null;
  }

  // ════════════════════════════════════════════════════════════════════
  //  SORT — partition items into bins by 1 then 2 attributes (productive)
  // ════════════════════════════════════════════════════════════════════
  function binMatches(bin, el) {
    for (var i = 0; i < bin.dims.length; i++) if (el[bin.dims[i]] !== bin.values[i]) return false;
    return true;
  }
  function genSort(level, rng) {
    var twoDim = level >= 6, items = [], answer = {}, bins = [], i;
    if (!twoDim) {
      var dim = pick(rng, ['color', 'shape']); // categorical only — size grouping reads as subtle
      var nbins = level <= 3 ? 2 : 3;
      var binVals = sample(rng, poolFor(dim), nbins);
      if (binVals.length < nbins) return null;
      bins = binVals.map(function (v) { return { dims: [dim], values: [v] }; });
      var N = clamp(4 + level, 5, 9);
      var assign = [];
      for (i = 0; i < N; i++) assign.push(i < nbins ? i : randint(rng, 0, nbins - 1)); // each bin non-empty
      assign = shuffle(rng, assign);
      for (i = 0; i < N; i++) {
        var b = assign[i], el = baseEl();
        el.shape = dim === 'shape' ? binVals[b] : pick(rng, SHAPES);
        el.color = dim === 'color' ? binVals[b] : pick(rng, COLORS);
        el.size = dim === 'size' ? binVals[b] : randint(rng, 1, 3);
        items.push(el); answer[i] = b;
      }
    } else {
      var dims2 = ['color', 'shape']; // both shown literally on each bin — clear 2x2 matrix
      var valsA = sample(rng, poolFor(dims2[0]), 2), valsB = sample(rng, poolFor(dims2[1]), 2);
      var combos = [];
      for (var a = 0; a < 2; a++) for (var bb = 0; bb < 2; bb++) { combos.push([valsA[a], valsB[bb]]); bins.push({ dims: dims2, values: [valsA[a], valsB[bb]] }); }
      var N2 = clamp(6 + Math.floor((level - 6) / 2), 6, 8);
      var assign2 = [];
      for (i = 0; i < N2; i++) assign2.push(i < 4 ? i : randint(rng, 0, 3));
      assign2 = shuffle(rng, assign2);
      for (i = 0; i < N2; i++) {
        var b2 = assign2[i], c = combos[b2], el2 = baseEl();
        el2[dims2[0]] = c[0]; el2[dims2[1]] = c[1];
        items.push(el2); answer[i] = b2;
      }
    }
    return {
      skill: 'sort', level: level, responseKind: 'map', family: twoDim ? 'sort2' : 'sort1',
      promptKey: twoDim ? 'prompt.sort2' : 'prompt.sort',
      stimulus: { kind: 'sort', items: items, bins: bins },
      answer: answer,
      rule: { type: 'sort', dims: bins[0].dims },
      hintKey: 'hint.sort', whyKey: 'why.sort'
    };
  }
  function validateSort(spec) {
    var items = spec.stimulus.items, bins = spec.stimulus.bins, ans = spec.answer, i, j;
    var binCounts = new Array(bins.length).fill(0);
    for (i = 0; i < items.length; i++) {
      var hit = -1, nmatch = 0;
      for (j = 0; j < bins.length; j++) if (binMatches(bins[j], items[i])) { nmatch++; hit = j; }
      if (nmatch !== 1) return 'item ' + i + ' matches ' + nmatch + ' bins';
      if (ans[i] !== hit) return 'answer for item ' + i + ' wrong';
      binCounts[hit]++;
    }
    for (j = 0; j < bins.length; j++) if (binCounts[j] === 0) return 'bin ' + j + ' empty';
    // a swapped assignment (two items from different bins) must check incorrect
    var pi = -1, pj = -1;
    for (i = 0; i < items.length && pj < 0; i++) for (j = i + 1; j < items.length; j++) if (ans[i] !== ans[j]) { pi = i; pj = j; break; }
    if (pj >= 0) { var bad = {}; for (i = 0; i < items.length; i++) bad[i] = ans[i]; var t = bad[pi]; bad[pi] = bad[pj]; bad[pj] = t; if (check(spec, bad).correct) return 'swapped assignment accepted'; }
    return null;
  }

  // ════════════════════════════════════════════════════════════════════
  //  MEMORY — attention & working memory (find-target / flash recall)
  // ════════════════════════════════════════════════════════════════════
  function memMatches(tgt, el) {
    if (el[tgt.d0] !== tgt.v0) return false;
    if (tgt.d1 && el[tgt.d1] !== tgt.v1) return false;
    return true;
  }
  function genMemory(level, rng) {
    if (level <= 4) {
      // find-target: tap all items that match a (possibly conjunctive) target
      var conj = level >= 3;
      var N = clamp(4 + level, 5, 9), i;
      var d0 = pick(rng, ['shape', 'color']);
      var v0 = pick(rng, poolFor(d0));
      var tgt = { d0: d0, v0: v0 };
      if (conj) { tgt.d1 = pick(rng, ['shape', 'color', 'size'].filter(function (x) { return x !== d0; })); tgt.v1 = pick(rng, poolFor(tgt.d1)); }
      var t = randint(rng, 1, N - 1);
      var pos = shuffle(rng, (function () { var o = []; for (i = 0; i < N; i++) o.push(i); return o; })());
      var isTarget = {}; for (i = 0; i < t; i++) isTarget[pos[i]] = 1;
      var items = [], answer = [];
      for (i = 0; i < N; i++) {
        var el = baseEl(); el.shape = pick(rng, SHAPES); el.color = pick(rng, COLORS); el.size = randint(rng, 1, 3);
        if (isTarget[i]) {
          el[d0] = v0; if (conj) el[tgt.d1] = tgt.v1; answer.push(i);
        } else {
          // must fail the target: flip d0 and/or d1
          if (!conj) { el[d0] = otherVal(rng, d0, v0); }
          else {
            var r = rng();
            if (r < 0.4) { el[d0] = otherVal(rng, d0, v0); el[tgt.d1] = tgt.v1; }
            else if (r < 0.8) { el[d0] = v0; el[tgt.d1] = otherVal(rng, tgt.d1, tgt.v1); }
            else { el[d0] = otherVal(rng, d0, v0); el[tgt.d1] = otherVal(rng, tgt.d1, tgt.v1); }
          }
        }
        items.push(el);
      }
      answer.sort(function (x, y) { return x - y; });
      return {
        skill: 'memory', level: level, responseKind: 'set', family: 'mfind',
        promptKey: conj ? 'prompt.memory_find_conj' : 'prompt.memory_find',
        stimulus: { kind: 'memory_find', items: items, target: tgt },
        answer: answer,
        rule: { type: 'memory', mode: 'find' },
        hintKey: 'hint.memory_find', whyKey: 'why.memory'
      };
    }
    // flash-recall: all K shown briefly, then one removed — which is missing?
    var K = clamp(3 + Math.floor((level - 5) / 2), 3, 6), j;
    var pool = [], seen = {}, guard = 0;
    while (pool.length < K && guard++ < 200) {
      var e = baseEl(); e.shape = pick(rng, SHAPES); e.color = pick(rng, COLORS); e.size = randint(rng, 1, 3);
      var key = elKey(e); if (seen[key]) continue; seen[key] = 1; pool.push(e);
    }
    if (pool.length < K) return null;
    var removed = randint(rng, 0, K - 1);
    var nowItems = [];
    for (j = 0; j < K; j++) if (j !== removed) nowItems.push(pool[j]);
    var flashMs = Math.max(2600 - level * 180, 900);
    return {
      skill: 'memory', level: level, responseKind: 'index', family: 'mflash',
      promptKey: 'prompt.memory_missing',
      stimulus: { kind: 'memory_flash', flashItems: pool, nowItems: nowItems, flashMs: flashMs },
      options: pool, answer: removed,
      rule: { type: 'memory', mode: 'missing' },
      hintKey: 'hint.memory_missing', whyKey: 'why.memory'
    };
  }
  function validateMemory(spec) {
    var r = spec.rule;
    if (r.mode === 'find') {
      var items = spec.stimulus.items, tgt = spec.stimulus.target, exp = [];
      for (var i = 0; i < items.length; i++) if (memMatches(tgt, items[i])) exp.push(i);
      if (exp.length < 1 || exp.length >= items.length) return 'find: target count out of range';
      if (exp.join(',') !== spec.answer.join(',')) return 'find: answer set mismatch';
      var dropped = spec.answer.slice(1);
      if (spec.answer.length && check(spec, dropped).correct) return 'find: incomplete set accepted';
      return null;
    }
    // flash-missing
    var pool = spec.stimulus.flashItems, now = spec.stimulus.nowItems;
    if (now.length !== pool.length - 1) return 'flash: nowItems wrong length';
    var rebuilt = [];
    for (var k = 0; k < pool.length; k++) if (k !== spec.answer) rebuilt.push(elKey(pool[k]));
    if (rebuilt.join('#') !== now.map(elKey).join('#')) return 'flash: nowItems != flash minus answer';
    return null;
  }

  // ───────────────────────── check() ─────────────────────────
  function check(spec, response) {
    switch (spec.responseKind) {
      case 'index':
        return { correct: response === spec.answer, expected: spec.answer };
      case 'set': {
        var a = (spec.answer || []).slice().sort(), b = (response || []).slice().sort();
        var ok = a.length === b.length && a.every(function (v, i) { return v === b[i]; });
        return { correct: ok, expected: spec.answer };
      }
      case 'order': {
        var r = response || [], e = spec.answer || [];
        var same = r.length === e.length && r.every(function (v, i) { return v === e[i]; });
        return { correct: same, expected: spec.answer };
      }
      case 'map': {
        var exp = spec.answer || {}, got = response || {}, all = true;
        for (var key in exp) if (exp[key] !== got[key]) { all = false; break; }
        return { correct: all, expected: spec.answer };
      }
      default:
        return { correct: false, expected: spec.answer };
    }
  }

  // ───────────────────────── universal invariants ─────────────────────────
  function inVocab(el) {
    if (el.cells) return Array.isArray(el.cells) && el.cells.length > 0 &&
      el.cells.every(function (c) { return Array.isArray(c) && c.length === 2; });
    if (el.shape != null && SHAPES.indexOf(el.shape) < 0) return false;
    if (el.color != null && COLORS.indexOf(el.color) < 0) return false;
    if (el.size != null && SIZES.indexOf(el.size) < 0) return false;
    if (el.fill != null && FILLS.indexOf(el.fill) < 0) return false;
    if (el.rot != null && ROTS.indexOf(el.rot) < 0) return false;
    if (el.count != null && (el.count < 1 || el.count > 9)) return false;
    if (el.icon != null && ICONS.indexOf(el.icon) < 0) return false;
    return true;
  }
  function universalInvariants(spec) {
    var errs = [];
    if (!spec || !spec.skill) return ['no spec'];
    if (!spec.promptKey) errs.push('no promptKey');
    if (spec.responseKind === 'index') {
      if (!Array.isArray(spec.options) || spec.options.length < 2) errs.push('need >=2 options');
      else {
        if (spec.answer < 0 || spec.answer >= spec.options.length) errs.push('answer index out of range');
        // Distinct options are required for "pick the right one" MCQs. Odd-one-out
        // intentionally repeats the group items (3 identical + 1 odd), so it opts out.
        if (spec.optionsDistinct !== false && uniqueByKey(spec.options).length !== spec.options.length) errs.push('duplicate options');
        for (var i = 0; i < spec.options.length; i++) if (!inVocab(spec.options[i])) errs.push('option out of vocab @' + i);
      }
    }
    // round-trip: the declared answer must check() as correct
    var resp = spec.responseKind === 'index' ? spec.answer : spec.answer;
    if (!check(spec, resp).correct) errs.push('declared answer fails check()');
    return errs;
  }

  // ───────────────────────── validate registry ─────────────────────────
  var VALIDATORS = {
    patterns: validatePatterns,
    oddone: validateOddone,
    analogy: validateAnalogy,
    quantity: validateQuantity,
    spatial: validateSpatial,
    deduce: validateDeduce,
    sort: validateSort,
    memory: validateMemory
  };
  function validate(spec) {
    var u = universalInvariants(spec);
    if (u.length) return { ok: false, errors: u };
    var v = VALIDATORS[spec.skill];
    var e = v ? v(spec) : null;
    return e ? { ok: false, errors: [e] } : { ok: true, errors: [] };
  }

  // ───────────────────────── generate dispatch (retry-or-throw) ─────────────────────────
  var GENERATORS = {
    patterns: genPatterns,
    oddone: genOddone,
    analogy: genAnalogy,
    quantity: genQuantity,
    spatial: genSpatial,
    deduce: genDeduce,
    sort: genSort,
    memory: genMemory
  };
  function generate(skill, level, seed) {
    var gen = GENERATORS[skill];
    if (!gen) throw genError(skill, 'unknown skill');
    level = clamp(level | 0 || 1, 1, MAX_LEVEL);
    var base = (seed == null) ? hashSeed(skill.length, level, 12345) : (seed >>> 0);
    for (var attempt = 0; attempt < 120; attempt++) {
      var rng = makeRng(hashSeed(base, attempt * 2654435761));
      var spec;
      try { spec = gen(level, rng); } catch (e) { continue; }
      if (!spec) continue;
      var res = validate(spec);
      if (res.ok) { spec.seed = base; return spec; }
    }
    throw genError(skill, 'could not build a valid puzzle at level ' + level + ' after 120 attempts');
  }
  // Generator that takes an explicit rng (used by the Node sweep harness).
  function generateWithRng(skill, level, rng) {
    var gen = GENERATORS[skill];
    if (!gen) throw genError(skill, 'unknown skill');
    level = clamp(level | 0 || 1, 1, MAX_LEVEL);
    for (var attempt = 0; attempt < 120; attempt++) {
      var spec;
      try { spec = gen(level, rng); } catch (e) { continue; }
      if (!spec) continue;
      var res = validate(spec);
      if (res.ok) return spec;
    }
    throw genError(skill, 'could not build a valid puzzle at level ' + level);
  }

  // ════════════════════════════════════════════════════════════════════
  //  ADAPTIVE CONTROLLER + SCORING (Zone of Proximal Development)
  // ════════════════════════════════════════════════════════════════════
  var RULES = { upStreak: 2, comfortFail: 2, masteryThresholds: [35, 65, 90] };
  function masteryFromLevel(level) { return clamp(Math.round(level / MAX_LEVEL * 100), 0, 100); }
  function starsFromMastery(m) { var t = RULES.masteryThresholds; return m >= t[2] ? 3 : m >= t[1] ? 2 : m >= t[0] ? 1 : 0; }
  function newSkillState(level) {
    var L = clamp(level || 1, 1, MAX_LEVEL), m = masteryFromLevel(L);
    return { level: L, attempts: 0, correct: 0, streak: 0, failStreak: 0, mastery: m, stars: starsFromMastery(m) };
  }
  // outcome ∈ 'solved_unaided' | 'solved_hinted' | 'failed'. Promote only on 2 unaided
  // wins; after 2 straight failures drop 2 levels and serve a guaranteed-winnable item
  // (the anti-frustration "comfort drop"). Never lets a child get stuck.
  function nextLevel(state, outcome) {
    var s = {
      level: clamp(state.level || 1, 1, MAX_LEVEL), attempts: (state.attempts || 0) + 1,
      correct: state.correct || 0, streak: state.streak || 0, failStreak: state.failStreak || 0
    };
    var action = 'hold';
    if (outcome === 'solved_unaided') {
      s.correct++; s.streak++; s.failStreak = 0;
      if (s.streak >= RULES.upStreak) { s.level = Math.min(MAX_LEVEL, s.level + 1); s.streak = 0; action = 'up'; }
    } else if (outcome === 'solved_hinted') {
      s.correct++; s.streak = 0; s.failStreak = 0; action = 'hold';
    } else {
      s.streak = 0; s.failStreak++;
      if (s.failStreak >= RULES.comfortFail) { s.level = Math.max(1, s.level - 2); s.failStreak = 0; action = 'comfort'; }
      else { s.level = Math.max(1, s.level - 1); action = 'down'; }
    }
    s.mastery = masteryFromLevel(s.level); s.stars = starsFromMastery(s.mastery);
    return { state: s, action: action };
  }
  // Suggest the next skill to practise: weakest mastery first, then least-practised.
  function pickSkill(progress) {
    var best = null;
    for (var i = 0; i < SKILLS.length; i++) {
      var sk = SKILLS[i], st = progress[sk] || newSkillState(1);
      var score = (st.mastery || 0) * 1000 + (st.attempts || 0);
      if (best === null || score < best.score) best = { skill: sk, score: score };
    }
    return best.skill;
  }

  // ════════════════════════════════════════════════════════════════════
  //  DIAGNOSTIC SKILL-MAP (turn "problems with logic" into per-skill bands)
  // ════════════════════════════════════════════════════════════════════
  var AGE_BASE6 = { patterns: 4, oddone: 4, sort: 3, analogy: 3, spatial: 3, deduce: 2, quantity: 3, memory: 3 };
  function expectedLevel(skill, age) { return clamp((AGE_BASE6[skill] || 3) + ((age || 6) - 6), 1, MAX_LEVEL); }
  function bandFor(estLevel, expected) { var d = estLevel - expected; return d >= 1 ? 'strong' : (d <= -2 ? 'needs' : 'ontrack'); }
  // results: { skill: { level, correct, total } } from the adaptive screener.
  function scoreDiagnostic(results, age) {
    age = clamp(age || 6, 5, 12);
    var map = {}, weak = [];
    for (var i = 0; i < SKILLS.length; i++) {
      var sk = SKILLS[i], r = results[sk] || { level: 1, correct: 0, total: 0 };
      var exp = expectedLevel(sk, age), b = bandFor(r.level, exp);
      map[sk] = { estLevel: r.level, mastery: masteryFromLevel(r.level), expected: exp, band: b, correct: r.correct || 0, total: r.total || 0 };
      weak.push({ skill: sk, band: b, gap: r.level - exp, mastery: map[sk].mastery });
    }
    weak.sort(function (a, b) { return (a.mastery - b.mastery) || (a.gap - b.gap); });
    return { skills: map, weakest: weak.slice(0, 3).map(function (w) { return w.skill; }), age: age };
  }

  // ════════════════════════════════════════════════════════════════════
  //  SHARE / DEEP-LINK (base36, no btoa — works in Node + browser)
  // ════════════════════════════════════════════════════════════════════
  function encodePuzzle(skill, level, seed) {
    return SKILLS.indexOf(skill).toString(36) + '.' + (level | 0).toString(36) + '.' + (seed >>> 0).toString(36);
  }
  function decodePuzzle(str) {
    var p = String(str || '').split('.'); if (p.length !== 3) return null;
    var si = parseInt(p[0], 36); if (isNaN(si) || si < 0 || si >= SKILLS.length) return null;
    return { skill: SKILLS[si], level: clamp(parseInt(p[1], 36) || 1, 1, MAX_LEVEL), seed: (parseInt(p[2], 36) || 0) >>> 0 };
  }
  function encodeReport(age, levels) {
    var s = '1' + clamp(age || 6, 3, 15).toString(36);
    for (var i = 0; i < SKILLS.length; i++) s += clamp((levels && levels[SKILLS[i]]) || 1, 1, MAX_LEVEL).toString(36);
    return s;
  }
  function decodeReport(str) {
    str = String(str || ''); if (str.charAt(0) !== '1' || str.length < 2 + SKILLS.length) return null;
    var age = parseInt(str.charAt(1), 36) || 6, levels = {};
    for (var i = 0; i < SKILLS.length; i++) levels[SKILLS[i]] = clamp(parseInt(str.charAt(2 + i), 36) || 1, 1, MAX_LEVEL);
    return { age: age, levels: levels };
  }

  return {
    // rng
    makeRng: makeRng, hashSeed: hashSeed,
    // vocab
    SHAPES: SHAPES, COLORS: COLORS, SIZES: SIZES, FILLS: FILLS, ROTS: ROTS, ICONS: ICONS,
    CATEGORIES: CATEGORIES, SKILLS: SKILLS, MAX_LEVEL: MAX_LEVEL, DIMS: DIMS,
    // core
    generate: generate, generateWithRng: generateWithRng,
    check: check, validate: validate,
    universalInvariants: universalInvariants,
    elKey: elKey,
    // adaptive + scoring
    RULES: RULES, newSkillState: newSkillState, nextLevel: nextLevel, pickSkill: pickSkill,
    masteryFromLevel: masteryFromLevel, starsFromMastery: starsFromMastery,
    // diagnostic
    expectedLevel: expectedLevel, scoreDiagnostic: scoreDiagnostic, AGE_BASE6: AGE_BASE6,
    // share / deep-link
    encodePuzzle: encodePuzzle, decodePuzzle: decodePuzzle, encodeReport: encodeReport, decodeReport: decodeReport,
    // exposed for tests
    _singletonReport: singletonReport, _fittingPredictions: fittingPredictions
  };
});
